import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Link } from "react-router-dom";
import valid from "./validation";
import {
  TextField,
  Box,
  Button,
  InputAdornment,
  FormControl,
  FormLabel,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@material-ui/core/";
import { HelpOutline } from "@material-ui/icons/";
import BackIcon from "src/assets/icons/web/backLink.svg";
import PasswordControl from "src/web/components/Form/PasswordControl";
import VisibilityPassword from "src/assets/icons/web/visibilityPassword.svg";
import VisibilityOffPassword from "src/assets/icons/web/visibilityOffPassword.svg";
import { useStyles } from "./styles";

export const ForgotPasswordForm = ({
  onSubmit,
  onCodeSubmit,
  confirmCode,
  setConfirmCode,
  isResettingPassword,
  isSendingCode,
}: any) => {
  const classes = useStyles();
  const { register, handleSubmit, errors, watch, control } = useForm({
    mode: "onChange",
  });
  const [showPassword, setShowPassword] = useState(false);

  const username = watch("username");

  const onFormSubmit = (data: any, e?: React.BaseSyntheticEvent) => {
    onSubmit(
      {
        username: data.username,
        confirmationCode: confirmCode,
        newPassword: data.newPassword,
      },
      e
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit(onFormSubmit)} className="w-full">
        <Box mb={3}>
          <TextField
            name="username"
            fullWidth
            className={classes.customInput}
            inputRef={register({
              required: {
                value: true,
                message: "Write your username",
              },
            })}
            label="Username"
            placeholder="Enter your username"
            InputProps={{
              startAdornment: <></>,
            }}
            variant="outlined"
            error={errors.username ? true : false}
            helperText={errors?.username?.message}
            size="small"
          />
        </Box>
        <Box mb={3}>
          <FormControl fullWidth>
            <FormLabel
              style={{
                color: "#2A2F40",
                marginBottom: "10px",
                fontSize: "14px",
                fontFamily: '"Montserrat", "Helvetica", "Arial", sans-serif',
              }}
            >
              Confirmation code{" "}
              <Tooltip
                title={`After you enter your username, please click the "send" button to receive your confirmation code which is sent to the admin email for your account. Enter this code in and then you can create a new password.`}
                interactive
                PopperProps={{ className: classes.tooltip }}
              >
                <IconButton size="small">
                  <HelpOutline
                    fontSize="small"
                    style={{ color: "rgb(196, 196, 196)" }}
                  />
                </IconButton>
              </Tooltip>
            </FormLabel>
            <TextField
              fullWidth
              className={classes.customInput}
              placeholder="Confirmation code"
              value={confirmCode || ""}
              onChange={(e) => setConfirmCode(e.target.value)}
              InputProps={{
                startAdornment: <></>,
                endAdornment: !confirmCode && (
                  <InputAdornment position="end">
                    <Box
                      onClick={() =>
                        !isSendingCode && username && onCodeSubmit(username)
                      }
                      className={classes.sendCodeButton}
                      style={{
                        background:
                          isSendingCode || !username
                            ? "#ccc"
                            : "linear-gradient(90deg, #06C9A4 0%, #00A2C1 100%)",
                        cursor:
                          isSendingCode || !username
                            ? "not-allowed"
                            : "pointer",
                        pointerEvents:
                          isSendingCode || !username ? "none" : "auto",
                      }}
                    >
                      {isSendingCode ? (
                        <CircularProgress size={14} style={{ color: "#fff" }} />
                      ) : (
                        "Send"
                      )}
                    </Box>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              size="small"
            />
          </FormControl>
        </Box>
        <Box mb={3}>
          <Controller
            name="newPassword"
            control={control}
            rules={valid.password}
            defaultValue=""
            render={(field, state) => {
              const { onChange, value, ref, ...restField } = field;
              return (
                <PasswordControl
                  error={state?.invalid ? (state as any).error?.message : false}
                  inputProps={{
                    ...restField,
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      onChange(e);
                    },
                    value: value,
                    ref,
                    className: classes.customInput,
                  }}
                  label="New password"
                  placeholder="Enter a new password"
                  name="newPassword"
                  showPassword={showPassword}
                  onTogglePassword={() => setShowPassword((prev) => !prev)}
                  VisibilityPassword={VisibilityPassword}
                  VisibilityOffPassword={VisibilityOffPassword}
                />
              );
            }}
          />
        </Box>
        <Button
          fullWidth={true}
          className={classes.authButton}
          variant="contained"
          color="primary"
          type="submit"
          disabled={isResettingPassword}
        >
          {isResettingPassword ? (
            <CircularProgress size={24} style={{ color: "#fff" }} />
          ) : (
            "Reset password"
          )}
        </Button>
      </form>
      <Box mt={1}>
        <Button
          fullWidth
          className={classes.backButton}
          variant="text"
          component={Link}
          to="/login"
        >
          <BackIcon style={{ marginRight: "8px" }} />
          <div style={{ fontSize: "14px" }}>Back to login page</div>
        </Button>
      </Box>
    </>
  );
};
